/**
 * Lazily loads the `algoliasearch` client library.
 *
 * `algoliasearch` is a heavy dependency (~40 KB gzipped). It used to be imported
 * statically at the top of the page reducers (LandingPage.duck / AlgoliaSearchPage
 * .duck), which are combined into the app's MAIN reducer bundle — so the library
 * was parsed and executed on every page load (including the landing page) even
 * though it is only needed when an Algolia request actually runs.
 *
 * Loading it through a dynamic import() moves it out of the critical initial
 * bundle (helps Lighthouse's "Reduce unused JavaScript" / "Reduce JavaScript
 * execution time"). The import() is memoized by the module system, so the chunk
 * is fetched at most once; creating the client object per call is negligible.
 * Behaviour is unchanged — callers just await the client instead of constructing
 * it synchronously.
 *
 * @param {string} appId  Algolia application id
 * @param {string} apiKey Algolia API key
 * @return {Promise<object>} the Algolia client
 */
export const getAlgoliaClient = async (appId, apiKey) => {
  const mod = await import(/* webpackChunkName: "algoliasearch" */ 'algoliasearch');
  const algoliasearch = mod.default || mod;
  return algoliasearch(appId, apiKey);
};
